import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';

import { FormattedMessage } from 'react-intl';

import { Text, TextType } from 'ui-components/components/text';
import { Button } from 'ui-components/components/button';

import { CircularProgressBar } from '../../../../components/circular-progress-bar';
import { Stepper } from '../../../../components/stepper';
import { useFTUWizardContext } from '../ftu-wizard/context/ftu-wizard-context';
import { messages } from './messages';
import style from './style.module.less';

const DIAMETER = 25;
const STROKE_WIDTH = 4;

/**
 * A banner component that displays setup progress and provides access to the setup wizard.
 * @param {object} props - The component props
 * @param {() => void} props.openDrawer - Function to open the setup drawer
 * @param {string[]} [props.stepLabels] - Array of labels for each step (default: [])
 * @param {number} [props.strokeWidth] - Width of the stroke for the CircularProgressBar (default: STROKE_WIDTH)
 * @param {number} [props.diameter] - Diameter of the CircularProgressBar (default: DIAMETER)
 * @returns {JSX.Element} The SetUpBanner component
 */
export const SetUpBanner = ({ openDrawer, stepLabels = [], strokeWidth = STROKE_WIDTH, diameter = DIAMETER }) => {
  const { getCurrentStepCount, restartWizard, isSetupWizardComplete } = useFTUWizardContext();
  const currentStepCount = getCurrentStepCount();
  const totalSteps = stepLabels.length;

  // For CircularProgressBar: show completed steps (current step - 1)
  // When wizard is complete, show all steps completed
  const completedStepsForCircular = isSetupWizardComplete ? totalSteps : Math.max(0, currentStepCount - 1);

  // For Stepper: show current step
  const stepperCurrentStep = isSetupWizardComplete ? totalSteps + 1 : currentStepCount;

  let buttonMessage;
  if (isSetupWizardComplete) {
    buttonMessage = messages.restartSetup;
  } else if (currentStepCount > 1) {
    buttonMessage = messages.finishSetup;
  } else {
    buttonMessage = messages.startSetup;
  }

  const handleButtonClick = async () => {
    if (isSetupWizardComplete) {
      await restartWizard();
    }
    openDrawer();
  };

  return (
    <div className={style['set-up-banner']}>
      <div className={style['set-up-greeting']}>
        <div className={style['set-up-greeting-text']}>
          <Text textType={TextType.H3} value={messages.welcome} />
          <Text textType={TextType.H1} value={messages.setUpShipping} />
        </div>
        <Button className={style['open-drawer-button']} onClick={handleButtonClick} theme='marketing'>
          <div className={style['setup-button-text']}>
            <FormattedMessage {...buttonMessage} />
          </div>
          <FontAwesomeIcon className={style['arrow-icon']} icon={faArrowRight} />
        </Button>
      </div>
      <div className={style['progress-section']}>
        <div className={style['progress-section-title']}>
          <Text textType={TextType.H3} value={messages.progressTitle} />
          <div className={style['circular-progress-container']}>
            <div className={style['progress-indicator']}>
              <CircularProgressBar
                completedSteps={completedStepsForCircular}
                diameter={diameter}
                strokeWidth={strokeWidth}
                totalSteps={totalSteps}
              />
            </div>
            <div className={style['progress-text']}>
              <Text
                formattedMessageValues={{
                  completedSteps: completedStepsForCircular,
                  totalSteps,
                }}
                textType={TextType.Paragraph}
                value={messages.progressText}
              />
            </div>
          </div>
        </div>
        <Stepper className={style.stepper} currentStep={stepperCurrentStep} stepLabels={stepLabels} totalSteps={totalSteps} />
      </div>
    </div>
  );
};
